import { useState, useEffect, useRef } from "react";

export function useMultiSearch(
  complianceProgramsData,
  controlsData,
  subprocessorsData,
  deliverablesData
) {
  const [searchTerm, setSearchTerm] = useState("");
  const [inputValue, setInputValue] = useState("");
  const [searchResults, setSearchResults] = useState({
    compliancePrograms: [],
    controls: [],
    subprocessors: [],
    deliverables: [],
  });

  const debounceTimeout = useRef(null);

  // Debounced search effect
  useEffect(() => {
    if (debounceTimeout.current) {
      clearTimeout(debounceTimeout.current);
    }

    debounceTimeout.current = setTimeout(() => {
      performSearch(inputValue);
    }, 300); // 300ms delay

    return () => {
      if (debounceTimeout.current) {
        clearTimeout(debounceTimeout.current);
      }
    };
  }, [inputValue]);

  const performSearch = (searchValue: string) => {
    if (!searchValue.trim()) {
      setSearchTerm("");
      setSearchResults({
        compliancePrograms: [],
        controls: [],
        subprocessors: [],
        deliverables: [],
      });
      return;
    }

    const searchLower = searchValue.toLowerCase();

    // Search compliance programs
    const matchedCompliancePrograms = complianceProgramsData.filter(
      (program: any) =>
        program.title.toLowerCase().includes(searchLower) ||
        program.description.toLowerCase().includes(searchLower)
    );

    // Search controls
    const matchedControls = controlsData.filter(
      (control: any) =>
        control.id.toLowerCase().includes(searchLower) ||
        control.description.toLowerCase().includes(searchLower)
    );

    // Search subprocessors
    const matchedSubprocessors = subprocessorsData.filter(
      (subprocessor: any) =>
        subprocessor.name.toLowerCase().includes(searchLower) ||
        subprocessor.description.toLowerCase().includes(searchLower)
    );

    // Search deliverables
    const matchedDeliverables = deliverablesData.filter(
      (deliverable: any) =>
        deliverable.title.toLowerCase().includes(searchLower) ||
        deliverable.description.toLowerCase().includes(searchLower)
    );

    setSearchTerm(searchValue);
    setSearchResults({
      compliancePrograms: matchedCompliancePrograms,
      controls: matchedControls,
      subprocessors: matchedSubprocessors,
      deliverables: matchedDeliverables,
    });
  };

  const onSearch = (value: string) => {
    setInputValue(value);
    // Also update immediately if it's empty to clear results
    if (!value.trim()) {
      performSearch(value);
    }
  };

  return {
    searchTerm,
    searchResults,
    onSearch,
  };
}

// Simplified hook for single data source searches
export function useSimpleSearch<T>(
  data: T[],
  searchFields: (keyof T)[],
  filterFn?: (item: T, searchTerm: string) => boolean
): {
  searchTerm: string;
  searchResults: T[];
  onSearch: (value: string) => void;
} {
  const [searchTerm, setSearchTerm] = useState("");
  const [inputValue, setInputValue] = useState("");
  const [searchResults, setSearchResults] = useState([]);

  const debounceTimeout = useRef(null);

  // Debounced search effect
  useEffect(() => {
    if (debounceTimeout.current) {
      clearTimeout(debounceTimeout.current);
    }

    debounceTimeout.current = setTimeout(() => {
      performSearch(inputValue);
    }, 300); // 300ms delay

    return () => {
      if (debounceTimeout.current) {
        clearTimeout(debounceTimeout.current);
      }
    };
  }, [inputValue]);

  const performSearch = (searchValue: string) => {
    if (!searchValue.trim()) {
      setSearchTerm("");
      setSearchResults([]);
      return;
    }

    const searchLower = searchValue.toLowerCase();

    let results: T[];
    results =
      (filterFn && data.filter((item) => filterFn(item, searchLower))) || [];
    // if (filterFn) {
    //   results = data.filter((item) => filterFn(item, searchLower));
    // } else {
    results = data.filter((item) =>
      searchFields.some((field) => {
        const fieldValue = item[field];
        if (typeof fieldValue === "string") {
          return fieldValue.toLowerCase().includes(searchLower);
        }
        return false;
      })
    );
    // }

    setSearchTerm(searchValue);
    setSearchResults(results);
  };

  const onSearch = (value: string) => {
    setInputValue(value);
    if (!value.trim()) {
      performSearch(value);
    }
  };

  return {
    searchTerm,
    searchResults,
    onSearch,
  };
}
